
import React, { useState, useEffect } from 'react';
import { LazyImage } from '../../../components/base/LazyImage';
import { SkeletonLoader, GallerySkeleton } from '../../../components/base/SkeletonLoader';
import { AnimatedSection } from '../../../components/base/AnimatedSection';

export const GallerySection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Simular carregamento inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const galleryImages = [
    {
      id: 1,
      src: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/a7fdf92b712f29643a40ed29bc2e9744.jpeg',
      alt: 'PPF - Paint Protection Film em veículo de luxo',
      category: 'PPF',
      description: 'Aplicação de Paint Protection Film em BMW'
    },
    {
      id: 2,
      src: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/e74622c26dc60a941142da13a06b310f.jpeg',
      alt: 'Vitrificação cerâmica em veículo premium',
      category: 'Vitrificação',
      description: 'Vitrificação cerâmica em Mercedes-Benz'
    },
    {
      id: 3,
      src: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/4040296cea06f887c0087822760b7711.jpeg',
      alt: 'Instalação de película automotiva',
      category: 'Película',
      description: 'Aplicação de película em Audi A4'
    },
    {
      id: 4,
      src: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/39c7a5581e02fec2e159351e71e1da3f.png',
      alt: 'Polimento profissional automotivo',
      category: 'Polimento',
      description: 'Polimento técnico em Porsche 911'
    },
    {
      id: 5,
      src: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/94f8b533eb56b64d43f8d6dde715abf5.png',
      alt: 'Higienização completa de interior',
      category: 'Higienização',
      description: 'Higienização completa em Range Rover'
    },
    {
      id: 6,
      src: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/59afa51d7e59df867c19289048eb8512.png',
      alt: 'Lavação detalhada premium',
      category: 'Lavação',
      description: 'Lavação detalhada em Tesla Model S'
    }
  ];

  if (isLoading) {
    return (
      <section className="py-20 bg-[#0D0D0D]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <SkeletonLoader variant="text" height="h-8" width="w-64" className="mx-auto mb-4" />
            <SkeletonLoader variant="text" height="h-4" width="w-96" className="mx-auto" />
          </div>
          <GallerySkeleton />
        </div>
      </section>
    );
  }

  return (
    <section id="galeria" className="py-20 bg-[#0D0D0D]">
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fadeIn" className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-white mb-4">
            <span className="text-[#C8A45D]">Galeria</span> de Trabalhos
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Confira alguns dos nossos trabalhos realizados com excelência e dedicação
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((image, index) => (
            <AnimatedSection
              key={image.id}
              animation="slideUp"
              delay={index * 100}
              className="group cursor-pointer"
              onClick={() => setSelectedImage(image.src)}
            >
              <div className="relative overflow-hidden rounded-xl bg-[#1C1C1C] border border-[#333] hover:border-[#C8A45D] transition-all duration-300 hover:scale-105">
                <LazyImage
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover"
                  width={600}
                  height={400}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block bg-[#C8A45D] text-black px-3 py-1 rounded-full text-sm font-semibold mb-2">
                      {image.category}
                    </span>
                    <p className="text-white text-sm font-medium">
                      {image.description}
                    </p>
                  </div>
                </div>

                {/* Zoom icon */}
                <div className="absolute top-4 right-4 bg-black/50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-white text-lg"></i>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Modal para visualização ampliada */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-[#C8A45D] transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
              <LazyImage
                src={selectedImage}
                alt="Imagem ampliada"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}

        <AnimatedSection animation="fadeIn" delay={600} className="text-center mt-12">
          <button
            onClick={() => window.open('https://wa.me/554888397243?text=Olá! Gostaria de ver mais trabalhos da Aura Estética Automotiva.', '_blank')}
            className="bg-gradient-to-r from-[#C8A45D] to-[#D4B76A] text-black px-8 py-3 rounded-lg font-semibold hover:from-[#D4B76A] hover:to-[#E0C376] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line mr-2"></i>
            Ver Mais Trabalhos
          </button>
        </AnimatedSection>
      </div>
    </section>
  );
};
